/**
 * Optional profile photo = the member's CCMS account avatar (AspNetUserDetail.Avatar).
 * Carpool stores nothing of its own: upload is the same flow as the ccms_v2 profile page —
 * FileManagement presign (domain "User", recordId = userId) → PUT the bytes straight to S3 →
 * confirm → `PUT users/me/avatar`. Removing sends an empty string (null means "unchanged").
 *
 * Before upload the image is decoded (EXIF orientation applied), drawn onto a canvas at most
 * 800 px on the long side and re-encoded as JPEG — which also strips every EXIF tag, GPS included.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth as useSdkAuth, useFileService, useUserService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { accountKeys, memberKeys, ridesKeys } from './queryKeys';
import { errorMessage } from './copy';
import { photoRules, useRidesSettings } from './settings';

export const PHOTO_MAX_EDGE = 800;
export const PHOTO_QUALITY = 0.85;
export const PHOTO_MIME = 'image/jpeg';
/** Originals beyond this are refused before decoding (protects phone memory). */
const MAX_SOURCE_MB = 40;

type FileServiceLike = ReturnType<typeof useFileService>;
type UserServiceLike = ReturnType<typeof useUserService>;

/** Phones / tablets get a second button that opens the camera directly (`capture`). */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  return coarse || /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

// ---------------------------------------------------------------- errors

export type PhotoErrorCode = 'not_image' | 'too_large' | 'decode_failed' | 'upload_failed' | 'signed_out';

/** A photo problem with a sentence the member can act on. */
export class PhotoError extends Error {
  readonly code: PhotoErrorCode;
  constructor(code: PhotoErrorCode, message: string) {
    super(message);
    this.name = 'PhotoError';
    this.code = code;
  }
}

const DECODE_FAILED = "We couldn't read that photo. Please try a JPG or PNG.";

/** Friendly text for anything thrown by preparePhoto / uploadAccountPhoto / updateMyAvatar. */
export function photoErrorMessage(err: unknown, fallback = "We couldn't save your photo. Please try again."): string {
  if (err instanceof PhotoError) return err.message;
  return errorMessage(err, fallback);
}

// ---------------------------------------------------------------- prepare (resize + strip EXIF)

export interface PreparedPhoto {
  blob: Blob;
  fileName: string;
  mimeType: string;
  width: number;
  height: number;
}

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp)$/i;

export function looksLikeImage(file: File): boolean {
  if (file.type) return file.type.toLowerCase().startsWith('image/');
  return IMAGE_EXT.test(file.name);
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

/** createImageBitmap with EXIF orientation applied; falls back to <img> (which modern browsers also orient). */
async function decodeImage(file: File): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bmp, width: bmp.width, height: bmp.height, release: () => bmp.close() };
    } catch {
      /* older Safari rejects the options bag or the format — try <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.decoding = 'async';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('decode'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch {
    URL.revokeObjectURL(url);
    throw new PhotoError('decode_failed', DECODE_FAILED);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new PhotoError('decode_failed', DECODE_FAILED))), type, quality);
  });
}

export interface EncodeImageOptions {
  /** Longest side after resizing (never upscales). */
  maxEdge: number;
  /** JPEG quality 0–1. */
  quality: number;
  /** Reject the RESULT when it is larger than this (MB). */
  maxMb: number;
  /** File name for the prepared blob. */
  fileName: string;
  /** Word used in error messages ("photo", "document"). */
  noun?: string;
}

/**
 * Decode (EXIF orientation applied) → at most `maxEdge` px on the long side → JPEG on a white
 * background. The re-encode drops all metadata (GPS, camera, timestamps). Shared by the profile
 * photo (800 px) and document uploads (2000 px, see lib/documents).
 */
export async function encodeImageAsJpeg(file: File, opts: EncodeImageOptions): Promise<PreparedPhoto> {
  const noun = opts.noun ?? 'photo';
  if (!file || !looksLikeImage(file)) {
    throw new PhotoError('not_image', "That file isn't a photo. Please choose a JPG, PNG or HEIC image.");
  }
  const limitMb = opts.maxMb > 0 ? opts.maxMb : 5;
  if (file.size > MAX_SOURCE_MB * 1024 * 1024) {
    throw new PhotoError('too_large', `That file is very large (over ${MAX_SOURCE_MB} MB). Please choose a smaller ${noun}.`);
  }

  const decoded = await decodeImage(file);
  try {
    const { width: w0, height: h0 } = decoded;
    if (!w0 || !h0) throw new PhotoError('decode_failed', DECODE_FAILED);

    const scale = Math.min(1, opts.maxEdge / Math.max(w0, h0));
    const width = Math.max(1, Math.round(w0 * scale));
    const height = Math.max(1, Math.round(h0 * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new PhotoError('decode_failed', DECODE_FAILED);
    // JPEG has no alpha: paint white first so transparent PNGs don't turn black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(decoded.source, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, PHOTO_MIME, opts.quality);
    // Release the backing store early (iOS caps total canvas memory).
    canvas.width = 0;
    canvas.height = 0;

    if (blob.size > limitMb * 1024 * 1024) {
      throw new PhotoError('too_large', `That ${noun} is still larger than ${limitMb} MB after resizing. Please choose a different one.`);
    }
    return { blob, fileName: opts.fileName, mimeType: PHOTO_MIME, width, height };
  } finally {
    decoded.release();
  }
}

/**
 * Decode → max 800 px long side → JPEG q0.85. The re-encode drops all metadata (GPS, camera,
 * timestamps). Rejects non-images and results larger than `maxMb`.
 */
export function preparePhoto(file: File, maxMb: number): Promise<PreparedPhoto> {
  return encodeImageAsJpeg(file, {
    maxEdge: PHOTO_MAX_EDGE,
    quality: PHOTO_QUALITY,
    maxMb,
    fileName: `profile-photo-${Date.now()}.jpg`,
  });
}

// ---------------------------------------------------------------- upload / remove

export type PhotoStage = 'idle' | 'preparing' | 'uploading' | 'saving' | 'removing';

export interface UploadAccountPhotoOptions {
  photo: PreparedPhoto;
  userId: string;
  files: FileServiceLike;
  users: UserServiceLike;
  onProgress?: (percent: number, stage: PhotoStage) => void;
}

/**
 * presign (domain "User", recordId = userId) → PUT to S3 (plain fetch, same Content-Type plus the
 * signed x-amz-acl: public-read header, no auth header) → confirm → PUT users/me/avatar. Returns the saved avatar URL.
 */
export async function uploadAccountPhoto({ photo, userId, files, users, onProgress }: UploadAccountPhotoOptions): Promise<string> {
  onProgress?.(20, 'uploading');
  const presigned = await files.presignUpload({
    fileName: photo.fileName,
    mimeType: photo.mimeType,
    domain: 'User',
    recordId: userId,
  });

  onProgress?.(35, 'uploading');
  let put: Response;
  try {
    put = await fetch(presigned.uploadUrl, {
      method: 'PUT',
      body: photo.blob,
      // FileManagement signs x-amz-acl=public-read into the presigned PUT (S3StorageService), so the
      // header is required (403 SignatureDoesNotMatch without it) and makes finalUrl publicly readable.
      headers: { 'Content-Type': photo.mimeType, 'x-amz-acl': 'public-read' },
    });
  } catch {
    throw new PhotoError('upload_failed', "The upload didn't go through. Check your connection and try again.");
  }
  if (!put.ok) {
    throw new PhotoError('upload_failed', "The upload didn't go through. Please try again.");
  }

  onProgress?.(75, 'saving');
  await files.confirmUpload({ fileId: presigned.fileId });

  onProgress?.(90, 'saving');
  const updated = await users.updateMyAvatar(presigned.finalUrl);
  onProgress?.(100, 'saving');
  const saved = updated?.avatar?.trim();
  return saved || presigned.finalUrl;
}

/** Clear the account avatar (empty string — null would mean "leave unchanged"). */
export async function removeAccountPhoto(users: UserServiceLike): Promise<void> {
  await users.updateMyAvatar('');
}

// ---------------------------------------------------------------- hooks

function cleanUrl(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

/** GET users/me — the account record that owns the avatar. */
export function useMyAccount() {
  const users = useUserService();
  const { isAuthenticated } = useEnrichedAuth();
  return useQuery({
    queryKey: accountKeys.me,
    queryFn: () => users.getCurrentUser(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export interface MyPhoto {
  url: string | null;
  hasPhoto: boolean;
  /** First users/me request still in flight (the auth-session avatar is used meanwhile). */
  isLoading: boolean;
}

/** The signed-in member's photo: users/me first, then the avatar on the auth session. */
export function useMyPhoto(): MyPhoto {
  const account = useMyAccount();
  const { state } = useSdkAuth();
  const url = account.isSuccess ? cleanUrl(account.data?.avatar) : cleanUrl(state.user?.avatar);
  return { url, hasPhoto: !!url, isLoading: account.isLoading };
}

export interface AccountPhotoActions {
  stage: PhotoStage;
  busy: boolean;
  progress: number;
  error: string | null;
  /** Local object URL of the photo just uploaded (shown instantly, no flash while S3 loads). */
  previewUrl: string | null;
  upload: (file: File) => Promise<boolean>;
  remove: () => Promise<boolean>;
  clearError: () => void;
}

/**
 * Upload / remove the account photo with stage + progress + inline error. On success the users/me
 * cache is patched, the "about me" queries are invalidated (rider/driver views carry photoUrl) and
 * the avatar on the SDK auth session is updated (so the shell avatar changes immediately).
 */
export function useAccountPhotoActions(): AccountPhotoActions {
  const files = useFileService();
  const users = useUserService();
  const { state, manager } = useSdkAuth();
  const qc = useQueryClient();
  const { settings } = useRidesSettings();
  const maxMb = photoRules(settings).maxMb;

  const [stage, setStage] = useState<PhotoStage>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewRef = useRef<string | null>(null);

  const replacePreview = useCallback((next: string | null) => {
    if (previewRef.current && previewRef.current !== next) URL.revokeObjectURL(previewRef.current);
    previewRef.current = next;
    setPreviewUrl(next);
  }, []);
  useEffect(() => () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); }, []);

  // AuthManager.updateUser persists the provider's state ref, which only catches up after React
  // commits. Once the new avatar is in state, persist once more so a reload keeps it.
  const pendingAuthAvatar = useRef<string | null>(null);
  const authAvatar = state.user?.avatar ?? '';
  useEffect(() => {
    const target = pendingAuthAvatar.current;
    if (target === null || authAvatar !== target || !manager) return;
    pendingAuthAvatar.current = null;
    window.setTimeout(() => manager.updateUser({ avatar: target }), 0);
  }, [authAvatar, manager]);

  const applyAvatar = useCallback(
    (avatar: string) => {
      qc.setQueryData(accountKeys.me, (old: unknown) => (old && typeof old === 'object' ? { ...(old as object), avatar } : old));
      qc.invalidateQueries({ queryKey: accountKeys.me });
      qc.invalidateQueries({ queryKey: ridesKeys.me.all });
      qc.invalidateQueries({ queryKey: memberKeys.me });
      if (manager && (state.user?.avatar ?? '') !== avatar) {
        pendingAuthAvatar.current = avatar;
        manager.updateUser({ avatar });
      }
    },
    [qc, manager, state.user?.avatar],
  );

  const upload = useCallback(
    async (file: File) => {
      setError(null);
      const userId = state.user?.userId;
      if (!userId) {
        setError('Please sign in again to change your photo.');
        return false;
      }
      setStage('preparing');
      setProgress(8);
      try {
        const photo = await preparePhoto(file, maxMb);
        replacePreview(URL.createObjectURL(photo.blob));
        const url = await uploadAccountPhoto({
          photo,
          userId,
          files,
          users,
          onProgress: (p, s) => { setProgress(p); setStage(s); },
        });
        applyAvatar(url);
        return true;
      } catch (e) {
        replacePreview(null);
        setError(photoErrorMessage(e));
        return false;
      } finally {
        setStage('idle');
        setProgress(0);
      }
    },
    [state.user?.userId, maxMb, files, users, applyAvatar, replacePreview],
  );

  const remove = useCallback(async () => {
    setError(null);
    setStage('removing');
    try {
      await removeAccountPhoto(users);
      replacePreview(null);
      applyAvatar('');
      return true;
    } catch (e) {
      setError(photoErrorMessage(e, "We couldn't remove your photo. Please try again."));
      return false;
    } finally {
      setStage('idle');
    }
  }, [users, applyAvatar, replacePreview]);

  return {
    stage,
    busy: stage !== 'idle',
    progress,
    error,
    previewUrl,
    upload,
    remove,
    clearError: useCallback(() => setError(null), []),
  };
}
